// src/context/BioDataContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import { getBioData } from '../services/firestore';
import { calculateImprovedAIME, calculatePIAFromAIME } from '../utils/socialSecurityUtils';
import { calculateEstimatedSalary } from '../utils/calculationUtils';

const BioDataContext = createContext();

export function useBioData() {
  return useContext(BioDataContext);
}

export function BioDataProvider({ children }) {
  const { user } = useUser();
  
  const [bioData, setBioData] = useState({
    dateOfBirth: '1980-04-04',
    entryOnDuty: '2004-05-05',
    currentGrade: 14,
    currentStep: 3,
    locality: 'DC',
    currentTspBalance: 50000,
    projectedSickLeave: 0,
    // Social Security fields
    pia: null, // Primary Insurance Amount (calculated automatically or manually)
    ssRetirementAge: 67, // Default SS retirement age
    isSSManualOverride: false // Flag to track if user has manually set SS values
  });

  // Load bio data when user logs in
  useEffect(() => {
    const loadBioData = async () => {
      if (user && !user.isGuest) {
        try {
          const savedBioData = await getBioData(user.uid);
          if (savedBioData) {
            setBioData(savedBioData);
          }
        } catch (error) {
          console.error('Error loading bio data:', error);
        }
      }
    };

    loadBioData();
  }, [user]);

  // Automatic Social Security calculation
  const calculateAutomaticSocialSecurity = useCallback((bioDataToCheck) => {
    // Only calculate if we have required data and no manual override
    if (!bioDataToCheck.isSSManualOverride && 
        bioDataToCheck.dateOfBirth && 
        bioDataToCheck.entryOnDuty && 
        bioDataToCheck.currentGrade && 
        bioDataToCheck.currentStep && 
        bioDataToCheck.locality) {
      
      try {
        // Calculate current salary from grade/step/locality
        const currentSalary = calculateEstimatedSalary(
          bioDataToCheck.currentGrade, 
          bioDataToCheck.currentStep, 
          bioDataToCheck.locality
        );
        
        // Create bioData with calculated salary for AIME calculation
        const bioDataWithSalary = {
          ...bioDataToCheck,
          currentSalary: currentSalary
        };
        
        const aimeResult = calculateImprovedAIME(bioDataWithSalary);
        if (aimeResult) {
          const pia = calculatePIAFromAIME(aimeResult.aime);
          return {
            pia: pia,
            ssRetirementAge: bioDataToCheck.ssRetirementAge || 67, // Keep existing or default
            isSSAutocalculated: true // Flag to indicate this was auto-calculated
          };
        }
      } catch (error) {
        console.error('Auto SS calculation error:', error);
      }
    }
    
    return null; // Return null if calculation not possible or manual override active
  }, []);

  // Auto-calculate Social Security on initial load if sufficient data exists
  useEffect(() => {
    // Removed problematic logging from useEffect per DEBUG_LOG.md rules

    if (!bioData.isSSManualOverride && !bioData.pia && 
        bioData.dateOfBirth && bioData.entryOnDuty && 
        bioData.currentGrade && bioData.currentStep && bioData.locality) {
      
      // Removed problematic logging from useEffect per DEBUG_LOG.md rules
      const autoSSResult = calculateAutomaticSocialSecurity(bioData);
      // Removed problematic logging from useEffect per DEBUG_LOG.md rules
      
      if (autoSSResult) {
        // Removed problematic logging from useEffect per DEBUG_LOG.md rules
        setBioData(prev => ({
          ...prev,
          ...autoSSResult
        }));
      }
    } else {
      // Removed problematic logging from useEffect per DEBUG_LOG.md rules
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bioData.dateOfBirth, bioData.entryOnDuty, bioData.currentGrade, bioData.currentStep, bioData.locality, bioData.isSSManualOverride, bioData.pia, calculateAutomaticSocialSecurity]);

  const handleBioChange = (field, value) => {
    setBioData(prev => {
      const newBioData = {
        ...prev,
        [field]: value
      };

      // Special handling for Social Security manual override detection
      if (field === 'pia' || field === 'ssRetirementAge') {
        // Mark as manual override when user explicitly sets SS values
        newBioData.isSSManualOverride = true;
        newBioData.isSSAutocalculated = false;
      }

      // If this is a field that affects SS calculation and no manual override is active
      if ((field === 'dateOfBirth' || field === 'entryOnDuty' || field === 'currentGrade' || field === 'currentStep' || field === 'locality') &&
          !prev.isSSManualOverride) {
        
        const autoSSResult = calculateAutomaticSocialSecurity(newBioData);
        if (autoSSResult) {
          Object.assign(newBioData, autoSSResult);
        }
      }

      return newBioData;
    });
  };

  // Calculate retirement age and years of service for a given retirement year
  const calculateRetirementStats = (retirementYear) => {
    let retirementAge = null;
    let yearsOfService = null;
    
    // Calculate retirement age from DOB
    if (bioData.dateOfBirth) {
      const birthDate = new Date(bioData.dateOfBirth);
      if (birthDate.getFullYear() >= 1900) {
        retirementAge = retirementYear - birthDate.getFullYear();
        
        // Adjust for month and day if birthday hasn't occurred yet in retirement year
        const birthMonth = birthDate.getMonth();
        const birthDay = birthDate.getDate();
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();
        
        if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
          retirementAge--;
        }
      }
    }
    
    // Calculate years of service from EOD
    if (bioData.entryOnDuty) {
      const eodDate = new Date(bioData.entryOnDuty);
      if (eodDate.getFullYear() >= 1900) {
        yearsOfService = retirementYear - eodDate.getFullYear();
        
        // Adjust for month and day
        const eodMonth = eodDate.getMonth();
        const eodDay = eodDate.getDate();
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();
        
        if (currentMonth < eodMonth || (currentMonth === eodMonth && currentDay < eodDay)) {
          yearsOfService--;
        }
      }
    }
    
    return { retirementAge, yearsOfService };
  };

  // Function to reset Social Security to automatic calculation
  const resetToAutomaticSS = useCallback(() => {
    setBioData(prev => {
      const newBioData = {
        ...prev,
        isSSManualOverride: false,
        isSSAutocalculated: false,
        pia: null
      };

      // Recalculate automatically
      const autoSSResult = calculateAutomaticSocialSecurity(newBioData);
      if (autoSSResult) {
        Object.assign(newBioData, autoSSResult);
      }

      return newBioData;
    });
  }, [calculateAutomaticSocialSecurity]);

  const value = {
    bioData,
    handleBioChange,
    calculateRetirementStats,
    resetToAutomaticSS
  };

  return (
    <BioDataContext.Provider value={value}>
      {children}
    </BioDataContext.Provider>
  );
}